import "./NotificationModals.css";
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from '@mui/material';
import { AppDispatch, RootState } from '../../../redux/store';
import { updateNotification, fetchNotificationById } from "../../../redux/actions/notifications.actions";
import { fetchDealerships } from "../../../redux/actions/dealership.actions";
import { fetchUsers } from "../../../redux/actions/users.actions";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import ModalShell from "../../../components/common/Modals/ModalShell";
import { CloseIcon } from "../../../components/common/Icons/Icons";

const UpdateNotificationModal: React.FC<ModalProps> = ({open, setOpen, object}) => {
    const notif = object;

    const handleClose = () => { handleReset(); setOpen(false); };
    const handleReset = () => { setSubmitted(false); };

    const dispatch = useDispatch<AppDispatch>();
    const { accessToken, data: user } = useSelector((state: RootState) => state.user);
    const { error, loading } = useSelector((state: RootState) => state.notifications);
    const { data: dealerships, loading: dealershipsLoading, meta: dealershipsMeta } = useSelector((state: RootState) => state.dealerships);
    const { data: users, loading: usersLoading, meta: usersMeta } = useSelector((state: RootState) => state.users);

    const canSendToAll = user?.permissions?.includes('can_send_notifications_to_all');
    const canSendToUsers = user?.permissions?.includes('can_send_notifs_to_users');
    const { canAccess: canViewDealerships } = usePermissionCheck(['view_dealerships']);

    const [submitted, setSubmitted] = useState(false);
    const [title, setTitle] = useState('');
    const [text, setText] = useState('');
    const [url, setUrl] = useState('');
    const [scheduledAt, setScheduledAt] = useState('');

    const [targetMode, setTargetMode] = useState<'dealerships' | 'users'>('dealerships');
    const [selectedDealerships, setSelectedDealerships] = useState<{id: number, name: string}[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<{id: number, name: string}[]>([]);
    const [searchInput, setSearchInput] = useState('');
    const [searchPage, setSearchPage] = useState(1);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (open && notif) {
        setTitle(notif.title ?? '');
        setText(notif.text ?? '');
        setUrl(notif.url ?? '');
        setScheduledAt(notif.scheduled_at ? notif.scheduled_at.slice(0, 16) : '');
        setSubmitted(false);
        setSelectedDealerships([]);
        setSelectedUsers([]);
        setSearchInput('');
        setSearchPage(1);
        setDropdownOpen(false);
        setTargetMode('dealerships');

        dispatch(fetchNotificationById(notif.id))
          .unwrap()
          .then((data) => {
            if (data.dealerships?.length) {
              setSelectedDealerships(data.dealerships.map((d: any) => ({ id: d.id, name: d.name })));
            }
            if (data.users?.length) {
              setSelectedUsers(data.users.map((u: any) => ({ id: u.id, name: u.email ?? u.name })));
              setTargetMode('users');
            }
          })
          .catch(() => {});
      }
    }, [open, notif, dispatch]);

    useEffect(() => {
      if (!dropdownOpen) return;
      if (targetMode === 'dealerships' && canViewDealerships) {
        dispatch(fetchDealerships(`name=${encodeURIComponent(searchInput)}&page=${searchPage}`));
      } else if (targetMode === 'users' && canSendToUsers) {
        dispatch(fetchUsers(`email=${encodeURIComponent(searchInput)}&page=${searchPage}`));
      }
    }, [searchInput, searchPage, dropdownOpen, targetMode, canViewDealerships, canSendToUsers, dispatch]);

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (inputRef.current && !inputRef.current.contains(event.target as Node) && !(event.target as HTMLElement).closest('.dealership-dropdown')) {
          setDropdownOpen(false);
        }
      }
      if (dropdownOpen) document.addEventListener('mousedown', handleClickOutside);
      else document.removeEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpen]);

    const handleSelect = (item: any) => {
      if (targetMode === 'dealerships') {
        if (!selectedDealerships.find(s => s.id === item.id)) {
          setSelectedDealerships([...selectedDealerships, { id: item.id, name: item.name }]);
        }
      } else {
        if (!selectedUsers.find(s => s.id === item.id)) {
          setSelectedUsers([...selectedUsers, { id: item.id, name: item.email ?? item.name }]);
        }
      }
      setSearchInput('');
      setDropdownOpen(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitted(true);

      // `image_key` is deliberately omitted: images can no longer be added or changed here,
      // and sending null would strip the image off any notification created before that.
      const data: any = {
        title,
        text,
        url: url || null,
        scheduled_at: scheduledAt || null,
      };

      if (targetMode === 'dealerships') {
        data.dealer_ids = selectedDealerships.length > 0
          ? selectedDealerships.map(d => d.id)
          : [user?.dealership_id];
      } else {
        data.user_ids = selectedUsers.map(u => u.id);
      }

      await dispatch(updateNotification({ id: notif.id, data }));
    };

    const dropdownItems = targetMode === 'dealerships' ? dealerships : users;
    const dropdownLoading = targetMode === 'dealerships' ? dealershipsLoading : usersLoading;
    const dropdownMeta = targetMode === 'dealerships' ? dealershipsMeta : usersMeta;
    const selectedItems = targetMode === 'dealerships' ? selectedDealerships : selectedUsers;
    const formDisabled = loading || !accessToken || !title || !text || (canViewDealerships && canSendToAll && targetMode === 'dealerships' && selectedDealerships.length === 0) || (canSendToUsers && targetMode === 'users' && selectedUsers.length === 0);
    const showForm = !submitted || loading;
    const visibleItems = (dropdownItems || []).filter((d: any) => d && d.id);

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size={showForm ? "md" : "sm"}
      busy={submitted && loading}
      tone={showForm ? undefined : error ? "error" : "success"}
      title={showForm ? "Edit notification" : error ? "An error occurred" : "Notification updated"}
      actions={showForm ?
        <>
          <button className="btn" onClick={handleClose} disabled={submitted && loading}>Cancel</button>
          <button className="btn btn-primary" disabled={formDisabled} onClick={handleSubmit}>
            {loading ? "Saving…" : "Save"}
          </button>
        </>
        : error ?
        <>
          <button className="btn" onClick={handleClose}>Close</button>
          <button className="btn btn-primary" onClick={handleReset}>Try again</button>
        </>
        :
        <button className="btn btn-primary" onClick={handleClose}>OK</button>
      }
    >
      {showForm ?
        <form className="ntf-form" onSubmit={handleSubmit}>
          <div className="field">
            <label className="field-label" htmlFor="edit-notif-title">Title</label>
            <input
              id="edit-notif-title"
              className="input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="edit-notif-text">Text</label>
            <textarea
              id="edit-notif-text"
              className="input ntf-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              required
              disabled={loading}
            />
          </div>
          <div className="dlg-row">
            <div className="field">
              <label className="field-label" htmlFor="edit-notif-url">URL (optional)</label>
              <input
                id="edit-notif-url"
                className="input"
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="edit-notif-schedule">Schedule at</label>
              <input
                id="edit-notif-schedule"
                className="input"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Target selection */}
          {canViewDealerships && canSendToAll ? (
            <div className="dlg-section">
              <span className="dlg-label">Send to</span>
              {canSendToUsers && (
                <div className="segmented" role="group" aria-label="Recipient type" style={{ alignSelf: 'flex-start' }}>
                  {(['dealerships', 'users'] as const).map(val => (
                    <button
                      key={val}
                      type="button"
                      className={targetMode === val ? 'active' : ''}
                      aria-pressed={targetMode === val}
                      onClick={() => {
                        if (val !== targetMode) {
                          setTargetMode(val);
                          setSearchInput('');
                          setSearchPage(1);
                          setDropdownOpen(false);
                          if (val === 'dealerships') setSelectedUsers([]);
                          if (val === 'users') setSelectedDealerships([]);
                        }
                      }}
                    >
                      {val === 'dealerships' ? 'Dealerships' : 'Users'}
                    </button>
                  ))}
                </div>
              )}

              <div className="ntf-search">
                <TextField
                  inputRef={inputRef}
                  label={targetMode === 'dealerships' ? 'Search dealerships' : 'Search users'}
                  variant="outlined"
                  size="small"
                  value={searchInput}
                  onFocus={() => setDropdownOpen(true)}
                  onChange={e => {
                    setSearchInput(e.target.value);
                    setSearchPage(1);
                    if (e.target.value !== '') setDropdownOpen(true);
                  }}
                  autoComplete="off"
                  fullWidth
                  disabled={loading}
                />
                {dropdownOpen && (
                  <div className="dealership-dropdown ntf-dropdown">
                    {dropdownLoading ? (
                      <div className="ntf-dropdown-empty">Loading…</div>
                    ) : (
                      <>
                        {visibleItems.length === 0 ? (
                          <div className="ntf-dropdown-empty">No results found</div>
                        ) : (
                          <ul className="ntf-dropdown-list">
                            {visibleItems.map((d: any) => (
                              <li key={d.id}>
                                <button
                                  type="button"
                                  className={`ntf-option ${selectedItems.find((s: any) => s.id === d.id) ? 'selected' : ''}`}
                                  onClick={() => handleSelect(d)}
                                >
                                  {targetMode === 'users' ? <>{d.email}{d.name && <span className="ntf-option-meta">— {d.name}</span>}</> : (d.name ?? `#${d.id}`)}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="ntf-pager">
                          <button type="button" className="btn btn-ghost btn-sm" disabled={searchPage <= 1} onClick={() => setSearchPage(searchPage - 1)}>Previous</button>
                          <button type="button" className="btn btn-ghost btn-sm" disabled={!(dropdownMeta && searchPage < dropdownMeta.total_pages)} onClick={() => setSearchPage(searchPage + 1)}>Next</button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              {selectedDealerships.length > 0 && targetMode === 'dealerships' && (
                <div className="ntf-chips">
                  {selectedDealerships.map(d => (
                    <span key={d.id} className="ntf-chip">
                      <span className="ntf-chip-label">{d.name}</span>
                      <button type="button" className="ntf-chip-remove" aria-label={`Remove ${d.name}`} onClick={() => setSelectedDealerships(selectedDealerships.filter(s => s.id !== d.id))}><CloseIcon/></button>
                    </span>
                  ))}
                </div>
              )}
              {selectedUsers.length > 0 && targetMode === 'users' && (
                <div className="ntf-chips">
                  {selectedUsers.map(u => (
                    <span key={u.id} className="ntf-chip">
                      <span className="ntf-chip-label">{u.name}</span>
                      <button type="button" className="ntf-chip-remove" aria-label={`Remove ${u.name}`} onClick={() => setSelectedUsers(selectedUsers.filter(s => s.id !== u.id))}><CloseIcon/></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="dlg-section">
              <div className="dlg-current">
                <span className="dlg-label">Dealership</span>
                <strong>{selectedDealerships.map(d => d.name).join(', ') || (user?.dealership?.name ?? `Dealership #${user?.dealership_id}`)}</strong>
              </div>
            </div>
          )}
        </form>
        : error ?
        <p>{error}</p>
        :
        <p>Notification successfully updated.</p>
      }
    </ModalShell>
  );
};

export default UpdateNotificationModal;
